import React, { useEffect, useMemo, useState } from "react";
import Section from "../components/Section";
import { INDIA_STATES } from "../lib/indiaStates";
import { supabase } from "../lib/supabaseClient";

const generateOrderNumber = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const rand = Math.floor(100 + Math.random() * 900);
  return `${yyyy}${mm}${dd}${hh}${mi}${rand}`;
};

export default function Checkout() {
  const [cart, setCart] = useState([]);
  const [cartReady, setCartReady] = useState(false);
  const [deliveryDestination, setDeliveryDestination] = useState("india");
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_note: "",
    shipping_address_line1: "",
    shipping_address_line2: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    landmark: "",
    notes: ""
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);
  const [submittedCart, setSubmittedCart] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [autoCheckMessage, setAutoCheckMessage] = useState("");
  const [paymentComplete, setPaymentComplete] = useState(false);

  const INTERNATIONAL_SHIPPING_FEE_SINGLE = 1600;
  const INTERNATIONAL_SHIPPING_FEE_DOUBLE = 2100;
  const isInternational = deliveryDestination === "international";

  useEffect(() => {
    const saved = localStorage.getItem("ss_cart");
    if (saved) {
      try {
        setCart(JSON.parse(saved));
      } catch (e) {
        setCart([]);
      }
    }
    // hydrate pending payment if user returned from Razorpay
    const pending = localStorage.getItem("ss_pending_payment");
    if (pending) {
      try {
        const parsed = JSON.parse(pending);
        if (parsed?.orderNumber && parsed?.paymentLink) {
          setSuccess({
            orderNumber: parsed.orderNumber,
            paymentLink: parsed.paymentLink,
            paymentLinkId: parsed.paymentLinkId || null,
            order: null
          });
          setSubmittedCart(parsed.cart || []);
        }
      } catch (e) {
        // ignore parse errors
      }
    }
    setCartReady(true);
  }, []);

  useEffect(() => {
    if (cartReady) {
      localStorage.setItem("ss_cart", JSON.stringify(cart));
    }
  }, [cart, cartReady]);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [cart]
  );

  const cartQuantity = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const displayCart = success ? submittedCart : cart;

  const displayCartTotal = useMemo(
    () => displayCart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [displayCart]
  );

  const displayCartQuantity = useMemo(
    () => displayCart.reduce((sum, item) => sum + item.quantity, 0),
    [displayCart]
  );

  const getInternationalShippingFee = (quantity) =>
    quantity <= 1 ? INTERNATIONAL_SHIPPING_FEE_SINGLE : INTERNATIONAL_SHIPPING_FEE_DOUBLE;

  const cartShippingFee = isInternational ? getInternationalShippingFee(cartQuantity) : 0;
  const displayShippingFee = isInternational ? getInternationalShippingFee(displayCartQuantity) : 0;
  const finalTotal = cartTotal + cartShippingFee;

  // Auto-check payment status periodically after link creation or on return
  useEffect(() => {
    let timer;
    let attempts = 0;

    const poll = async () => {
      if (!success?.paymentLink || !success?.orderNumber) return;
      setAutoCheckMessage("Verifying payment status...");
      try {
        const { data, error } = await supabase.functions.invoke("verify-payment", {
          body: {
            payment_link_id: success.paymentLinkId,
            reference: success.orderNumber
          }
        });
        if (error) throw error;
        if (data?.status === "paid") {
          try {
            await supabase
              .from("orders")
              .update({
                payment_status: "paid",
                payment_link_status: data.status || null,
                razorpay_payment_id: data.payment_id || null,
                razorpay_signature: data.signature || null,
                payment_provider: "razorpay"
              })
              .eq("order_number", success.orderNumber);
          } catch (updateErr) {
            console.error("Failed to update order payment fields", updateErr);
          }
          setPaymentStatus(`Payment verified. Order marked as paid. Order ID: ${success.orderNumber}`);
          setPaymentComplete(true);
          setSubmittedCart([]);
          setCart([]);
          localStorage.removeItem("ss_pending_payment");
          localStorage.removeItem("ss_cart");
          setTimeout(() => {
            window.location.href = "/shop";
          }, 1200);
          return; // stop polling
        } else {
          setPaymentStatus(`Current status: ${data?.status || "pending"}`);
        }
      } catch (err) {
        setPaymentStatus(err?.message || "Failed to verify payment.");
      }

      attempts += 1;
      if (attempts < 6) {
        timer = window.setTimeout(poll, 4000);
      } else {
        setAutoCheckMessage(
          `Your payment is still in progress. Order ID: ${success.orderNumber}. Please check again later.`
        );
        localStorage.removeItem("ss_pending_payment");
      }
    };

    if (success?.paymentLink) {
      timer = window.setTimeout(poll, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [success]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDestinationChange = (event) => {
    const nextDestination = event.target.value;
    setDeliveryDestination(nextDestination);
    if (nextDestination === "india") {
      setForm((prev) => ({
        ...prev,
        country: "India",
        state: ""
      }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const trimmedPhone = form.customer_phone.trim();
    const digitsOnly = trimmedPhone.replace(/[^0-9]/g, "");

    if (!form.customer_name.trim()) nextErrors.customer_name = "Name is required.";
    if (!trimmedPhone) {
      nextErrors.customer_phone = "Phone is required.";
    } else if (!isInternational && !/^[0-9]{10}$/.test(trimmedPhone)) {
      nextErrors.customer_phone = "Enter a valid 10-digit phone.";
    } else if (isInternational && (digitsOnly.length < 7 || digitsOnly.length > 15)) {
      nextErrors.customer_phone = "Enter a valid international phone with country code.";
    } else if (isInternational && !/^\+?[0-9\s-]+$/.test(trimmedPhone)) {
      nextErrors.customer_phone = "Use only numbers, spaces, dashes, and an optional leading +.";
    }
    if (form.customer_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email.trim())) {
      nextErrors.customer_email = "Enter a valid email.";
    }
    if (!form.shipping_address_line1.trim()) nextErrors.shipping_address_line1 = "Address is required.";
    if (!form.city.trim()) nextErrors.city = "City is required.";
    if (!form.state.trim()) nextErrors.state = "State is required.";
    if (!form.country.trim()) nextErrors.country = "Country is required.";
    if (!form.pincode.trim()) {
      nextErrors.pincode = isInternational ? "Postal code is required." : "Pincode is required.";
    } else if (!isInternational && !/^[0-9]{6}$/.test(form.pincode.trim())) {
      nextErrors.pincode = "Enter a valid 6-digit pincode.";
    }
    if (isInternational && cartQuantity > 2) {
      nextErrors.cart = "International orders are limited to 2 total items.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (cart.length === 0) {
      setSubmitError("Your cart is empty.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const orderNumber = generateOrderNumber();
      const paymentLink = "";
      let paymentLinkWithRef = paymentLink;

      const notesSummary = cart
        .map((item) => {
          const sku = item.product.product_sku || "SKU N/A";
          const variantLabel =
            item.variant_label ||
            item.variant?.label_value ||
            item.variant?.color ||
            item.variant?.size ||
            item.variant?.label ||
            "";
          const variantSku = item.variant?.variant_sku || "";
          const variantInfo = variantLabel ? ` | Variant: ${variantLabel}` : "";
          const variantSkuInfo = variantSku ? ` | Variant SKU: ${variantSku}` : "";
          return `${item.product.name} | SKU: ${sku}${variantInfo}${variantSkuInfo} | Qty: ${item.quantity} | Line Total: ${item.unitPrice * item.quantity}`;
        })
        .join(" || ");

      const firstProduct = cart[0]?.product?.name || "Order";
      const productName = cart.length > 1 ? `${firstProduct} +${cart.length - 1} more` : `${firstProduct}`;
      const songRequest = form.customer_note.trim();
      const customerNote = songRequest
        ? `${songRequest} | ${form.customer_name.trim()} | ${orderNumber}`
        : null;

      const payload = {
        order_number: orderNumber,
        product_id: cart[0]?.product.id || null,
        product_name: productName,
        unit_price_inr: cartTotal,
        quantity: 1,
        total_amount_inr: finalTotal,
        payment_link_used: paymentLinkWithRef || paymentLink || null,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim() || null,
        shipping_address_line1: form.shipping_address_line1.trim(),
        shipping_address_line2: form.shipping_address_line2.trim() || null,
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country.trim(),
        pincode: form.pincode.trim(),
        landmark: form.landmark.trim() || null,
        customer_note: customerNote,
        notes: `${notesSummary}\n${form.notes || ""}`,
        international_shipping: isInternational
      };

      const { error: insertError } = await supabase.from("orders").insert(payload).select().single();
      if (insertError) throw insertError;

      setSubmittedCart(cart);

      // Create Razorpay payment link via edge function using exact cart total
      try {
        const { data: linkData, error: linkError } = await supabase.functions.invoke("create-payment-link", {
          body: {
            amountInr: finalTotal,
            reference: orderNumber,
            customer: {
              name: form.customer_name,
              email: form.customer_email,
              contact: form.customer_phone
            },
            notes: { order_number: orderNumber }
          }
        });
        if (linkError) throw new Error(linkError.message || linkError.error || "Edge function request failed");

        const shortUrl =
          linkData?.short_url ||
          linkData?.data?.short_url ||
          null;

        if (shortUrl) {
          const linkId =
            linkData?.payment_link_used ||
            linkData?.payment_link_id ||
            linkData?.id ||
            linkData?.data?.id ||
            linkData?.data?.payment_link_id ||
            linkData?.payment_link_used ||
            linkData?.data?.payment_link_used ||
            (() => {
              const parts = shortUrl?.split("/").filter(Boolean) || [];
              const last = parts[parts.length - 1] || "";
              if (!last) return null;
              return last.startsWith("plink_") ? last : last;
            })();

          const { error: updateError } = await supabase
            .from("orders")
            .update({
              payment_link_used: linkId || shortUrl || null, // prefer plink id, else fallback to short url
              razorpay_order_id: linkId || null
            })
            .eq("order_number", orderNumber);
          if (updateError) {
            console.error("Failed to update payment link fields", updateError);
            setSubmitError(updateError.message || "Failed to save payment link info.");
          }

          // save pending payment for return flow
          localStorage.setItem(
            "ss_pending_payment",
            JSON.stringify({
              orderNumber,
              paymentLink: shortUrl,
              paymentLinkId: linkId || null,
              cart
            })
          );

          // clear cart storage once payment is initiated
          setCart([]);
          localStorage.removeItem("ss_cart");

          // open payment link in a new tab so this page can keep polling
          window.open(linkData.short_url, "_blank", "noopener");

          // set success state to start polling
          setSuccess((prev) => ({
            orderNumber,
            paymentLink: shortUrl,
            paymentLinkId: linkId || null,
            order: prev?.order || null
          }));
          setSubmittedCart(cart);
          setAutoCheckMessage("Payment link opened in a new tab. Complete payment to confirm.");
          return;
        } else {
          setSubmitError("Payment link not returned. Please try again.");
        }
      } catch (err) {
        // Surface edge function failures so users see the reason
        const msg =
          err?.message ||
          (typeof err === "string" ? err : "") ||
          "Failed to start payment (edge function).";
        setSubmitError(msg);
      }
    } catch (err) {
      setSubmitError(err?.message || "Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {!paymentComplete && (
        <Section
          eyebrow="Checkout"
          title="Delivery details"
          description="Review your cart and confirm delivery information."
        >
          {displayCart.length === 0 ? (
            <div className="text-center text-gray-300">Your cart is empty. Add items from the shop.</div>
          ) : (
            <div className="grid gap-4">
              {displayCart.map((item, idx) => (
                <div
                  key={`${item.product.id}-base-${idx}`}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="h-16 w-16 rounded-xl overflow-hidden bg-black/30 border border-white/10 flex-shrink-0 self-start sm:self-auto">
                    {item.variant?.image_url || item.product.image_url ? (
                      <img
                        src={item.variant?.image_url || item.product.image_url}
                        alt={item.product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">{item.product.name}</div>
                    <div className="text-xs text-gray-300">
                      {item.product.product_type || ""} {item.product.product_sku ? `— ${item.product.product_sku}` : ""}
                    </div>
                    {(item.variant_label || item.variant?.label_value || item.variant?.color || item.variant?.size || item.variant?.label) ? (
                      <div className="text-xs text-amber-200">
                        Variant: {item.variant_label || item.variant?.label_value || item.variant?.color || item.variant?.size || item.variant?.label}
                      </div>
                    ) : null}
                    <div className="text-xs text-gray-400">Qty: {item.quantity}</div>
                  </div>
                  <div className="text-amber-300 font-semibold sm:self-start">₹{item.unitPrice * item.quantity}</div>
                  {!success ? (
                    <button
                      onClick={() => setCart((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-xs text-gray-300 hover:text-amber-200"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white">
                <span className="font-semibold">Total</span>
                <span className="text-amber-300 font-bold text-lg">
                  ₹
                  {success ? displayCartTotal + displayShippingFee : finalTotal}
                </span>
              </div>
              {isInternational ? (
                <div className="text-xs text-amber-200/90 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-2">
                  International delivery fee: ₹{INTERNATIONAL_SHIPPING_FEE_SINGLE} (1 item) or ₹{INTERNATIONAL_SHIPPING_FEE_DOUBLE} (2 items). Orders limited to 2 total items.
                </div>
              ) : null}
            </div>
          )}
        </Section>
      )}

      {!paymentComplete && displayCart.length > 0 ? (
        <Section>
          <div className="space-y-5">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold text-white mb-2">Delivery destination</div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-gray-200">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="delivery_destination"
                  value="india"
                  checked={deliveryDestination === "india"}
                  onChange={handleDestinationChange}
                />
                India
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="delivery_destination"
                  value="international"
                  checked={deliveryDestination === "international"}
                  onChange={handleDestinationChange}
                />
                International
              </label>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <FormField
              label="Name"
              name="customer_name"
              value={form.customer_name}
              onChange={handleChange}
              error={errors.customer_name}
              required
            />
            <FormField
              label="Phone"
              name="customer_phone"
              value={form.customer_phone}
              onChange={handleChange}
              error={errors.customer_phone}
              required
              placeholder={isInternational ? "e.g. +1 415 555 0198" : "10-digit mobile"}
            />
          </div>

          <FormField
            label="Email"
            name="customer_email"
            value={form.customer_email}
            onChange={handleChange}
            error={errors.customer_email}
            placeholder="Optional"
          />

          <FormField
            label="Address Line 1"
            name="shipping_address_line1"
            value={form.shipping_address_line1}
            onChange={handleChange}
            error={errors.shipping_address_line1}
            required
          />
          <FormField
            label="Address Line 2"
            name="shipping_address_line2"
            value={form.shipping_address_line2}
            onChange={handleChange}
            error={errors.shipping_address_line2}
            placeholder="Apartment, suite, etc. (optional)"
          />

          <FormField
            label="Country"
            name="country"
            value={form.country}
            onChange={handleChange}
            error={errors.country}
            required
            placeholder={isInternational ? "Country" : "India"}
          />

          <div className="grid md:grid-cols-2 gap-5">
            <FormField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
              error={errors.city}
              required
            />
            {isInternational ? (
              <FormField
                label="State / Province"
                name="state"
                value={form.state}
                onChange={handleChange}
                error={errors.state}
                required
                placeholder="State or province"
              />
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-white">
                  State<span className="text-amber-300">*</span>
                </label>
                <select
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  className="w-full rounded-xl bg-black/40 border border-white/20 px-4 py-3.5 text-white focus:border-amber-300 outline-none"
                >
                  <option value="">Select state</option>
                  {INDIA_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                {errors.state ? <p className="text-xs text-red-400 mt-1">{errors.state}</p> : null}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <FormField
              label={isInternational ? "Postal code" : "Pincode"}
              name="pincode"
              value={form.pincode}
              onChange={handleChange}
              error={errors.pincode}
              required
              placeholder={isInternational ? "Postal code" : "6-digit"}
            />
            <FormField
              label="Landmark"
              name="landmark"
              value={form.landmark}
              onChange={handleChange}
              error={errors.landmark}
              placeholder="Optional"
            />
          </div>

          <FormField
            label="🎵 Song Request for Live Show (1 song per order)"
            name="customer_note"
            value={form.customer_note}
            onChange={handleChange}
            error={errors.customer_note}
            placeholder="Song name (optional)"
          />
          <p className="text-xs text-gray-300 leading-relaxed">
            Your song will be sung live during the Facebook session once payment is confirmed. Only orders placed during the LIVE session will be considered.
          </p>

          <FormField
            label="Notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            error={errors.notes}
            placeholder="Any special instructions (optional)"
          />
          </div>

          {errors.cart ? (
            <div className="text-sm text-red-400 bg-red-900/30 border border-red-500/30 rounded-xl px-3 py-2">
              {errors.cart}
            </div>
          ) : null}

          {submitError ? (
            <div className="text-sm text-red-400 bg-red-900/30 border border-red-500/30 rounded-xl px-3 py-2">
              {submitError}
            </div>
          ) : null}

          {success ? (
          <div className="space-y-3 mt-4">
            <div className="rounded-2xl border border-green-400/30 bg-green-900/20 px-4 py-3 text-green-200">
              Order placed! Reference: <strong>{success.orderNumber}</strong>
            </div>
            {success.paymentLink && !paymentComplete ? (
              <div className="flex flex-wrap gap-3">
                <a
                  href={success.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex justify-center rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg"
                >
                  Proceed to Pay
                </a>
              </div>
            ) : null}
            {paymentStatus ? (
              <div className="text-sm text-gray-200 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                {paymentStatus}
              </div>
            ) : null}
            {autoCheckMessage ? (
              <div className="text-xs text-gray-400">{autoCheckMessage}</div>
            ) : null}
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg disabled:opacity-60"
            >
              {submitting ? "Placing order..." : "Place order"}
            </button>
          )}
        </Section>
      ) : null}
    </div>
  );
}

function FormField({ label, name, value, onChange, error, required, placeholder, className }) {
  return (
    <div className={`space-y-2 ${className || ""}`}>
      <label className="block text-sm font-semibold text-white">
        {label}
        {required ? <span className="text-amber-300">*</span> : null}
      </label>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl bg-black/40 border border-white/20 px-4 py-3.5 text-white focus:border-amber-300 outline-none"
      />
      {error ? <p className="text-xs text-red-400 mt-1">{error}</p> : null}
    </div>
  );
}
